import React, { useState, useEffect } from 'react';
import { auth } from '../firebase/config';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';

// Gmail whitelist - ONLY these accounts can access the system
// NOTE: This is a CLIENT-SIDE check for UX purposes only!
// The REAL security check happens on the backend (app.py)
// Keep this list in sync with backend ALLOWED_EMAILS
const WHITELIST = [
  "kyungwanhan@gmail.com",
  "llm01.cviss@gmail.com",
  // Add more authorized emails here
];

function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Check if user is already signed in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && WHITELIST.includes(user.email)) {
        console.log('User already authenticated:', user.email);
        // App.js will handle the redirect
      }
    });

    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setMessage('Signing in...');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log('User signed in:', user.email);

      // Check if user email is in whitelist
      if (!WHITELIST.includes(user.email)) {
        // Not authorized - sign out immediately
        await auth.signOut();
        setError(`Access denied. Your account (${user.email}) is not authorized.`);
        setMessage('');
        setLoading(false);
        return;
      }

      // User is authorized
      setMessage(`Welcome, ${user.displayName || user.email}!`);
      // App.js will handle the redirect via onAuthStateChanged
    } catch (err) {
      console.error('Sign in error:', err);

      let errorMessage = 'Sign in failed. ';
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          errorMessage += 'Sign-in popup was closed.';
          break;
        case 'auth/popup-blocked':
          errorMessage += 'Sign-in popup was blocked by browser.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage += 'Another sign-in request is in progress.';
          break;
        default:
          errorMessage += err.message;
      }

      setError(errorMessage);
      setMessage('');
      setLoading(false);
    }
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        background: 'white',
        padding: '50px 40px',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>
          🔐
        </div>

        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          color: '#667eea',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}>
          Autonomous Inspection System
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#1a1a1a',
          margin: '0 0 8px 0',
          letterSpacing: '-0.5px',
          lineHeight: '1.2'
        }}>
          CVISS × OPG
        </h1>

        <p style={{
          fontSize: '15px',
          color: '#667eea',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          3D Waypoint Navigation Demo
        </p>

        <p style={{
          fontSize: '12px',
          color: '#999',
          marginBottom: '30px',
          lineHeight: '1.5'
        }}>
          Powered by Gaussian Splatting & AI Path Planning
        </p>

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: '100%',
            background: 'white',
            color: '#444',
            border: '1px solid #ddd',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'all 0.3s',
            opacity: loading ? 0.5 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#f8f8f8';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
            <path fill="none" d="M0 0h48v48H0z" />
          </svg>
          Sign in with Google
        </button>

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '12px',
            background: '#e8f5e9',
            color: '#2e7d32',
            borderLeft: '4px solid #2e7d32',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '20px',
            padding: '12px',
            background: '#ffebee',
            color: '#c62828',
            borderLeft: '4px solid #c62828',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div style={{
          marginTop: '35px',
          paddingTop: '20px',
          borderTop: '1px solid #eee'
        }}>
          <div style={{
            color: '#aaa',
            fontSize: '11px',
            marginBottom: '5px'
          }}>
            🔒 Secure Access · Authorized Personnel Only
          </div>
          <div style={{
            color: '#ccc',
            fontSize: '10px'
          }}>
            © 2025 CVISS Lab · Advanced Robotics & AI
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
